import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { trademarkClasses, searchTrademarkClasses } from '../data/trademarkClasses';

const ClassAutocomplete = ({ 
  classNumber,
  classDescription,
  onClassNumberChange,
  onClassDescriptionChange,
  onClassChange, // New single callback for both fields
  onAddClass, // New callback for adding class
  placeholder = "Search for trademark class...",
  error = false,
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Virtual scrolling configuration
  const ITEM_HEIGHT = 80; // Approximate height of each class item
  const VISIBLE_ITEMS = 8; // Number of items visible at once
  const BUFFER_ITEMS = 4; // Extra items to render for smooth scrolling

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_ITEMS);
  const endIndex = Math.min(
    filteredClasses.length,
    Math.ceil(scrollTop / ITEM_HEIGHT) + VISIBLE_ITEMS + BUFFER_ITEMS
  );

  // Get visible items
  const visibleItems = useMemo(() => {
    return filteredClasses.slice(startIndex, endIndex);
  }, [filteredClasses, startIndex, endIndex]);

  // Calculate total height for scroll container
  const totalHeight = filteredClasses.length * ITEM_HEIGHT;
  const offsetY = startIndex * ITEM_HEIGHT;

  useEffect(() => {
    // Set initial value if provided
    if (classNumber) {
      const classData = trademarkClasses.find(cls => cls.number === parseInt(classNumber));
      if (classData) {
        setSelectedClass(classData);
        setSearchTerm(`Class ${classData.number}`);
      }
    }
  }, [classNumber]);

  useEffect(() => {
    // Filter classes based on search term
    const filtered = searchTrademarkClasses(searchTerm);
    setFilteredClasses(filtered);
    setScrollTop(0); // Reset scroll position when search changes
  }, [searchTerm]);

  useEffect(() => {
    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setScrollTop(1);
    if (!value) {
      setSelectedClass(null);
    }
  };

  const handleClassSelect = (classData) => {
    setSelectedClass(classData);
    setSearchTerm(`Class ${classData.number}`);
    setIsOpen(false);
    
    // Update both fields together to prevent race conditions
    const classNumber = parseInt(classData.number);
    
    if (onClassChange) {
      // Use single callback if available
      onClassChange(classNumber, classData.description);
    } else {
      // Fall back to separate callbacks
      onClassNumberChange(classNumber);
      onClassDescriptionChange(classData.description);
    }
  };

  const handleAddClass = (e, classData) => {
    e.stopPropagation(); // Prevent triggering the select event
    
    if (onAddClass) {
      onAddClass(classData);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && filteredClasses.length > 0) {
      handleClassSelect(filteredClasses[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-300' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown with Virtual Scrolling */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-hidden">
          {filteredClasses.length > 0 ? (
            <div 
              ref={dropdownRef}
              className="overflow-auto max-h-96"
              onScroll={handleScroll}
              style={{ height: `${Math.min(filteredClasses.length * ITEM_HEIGHT, 24 * 16)}px` }}
            >
              <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
                <div style={{ transform: `translateY(${offsetY}px)` }}>
                  {visibleItems.map((classData, index) => {
                    const actualIndex = startIndex + index;
                    return (
                      <div
                        key={`${classData.number}-${classData.name}`}
                        onClick={() => handleClassSelect(classData)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        
                      >
                        <div className="flex items-start justify-between h-full">
                          <div className="flex items-start flex-1">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                              <span className="text-xs font-medium text-blue-600">{classData.number}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 items-center">
                                Class {classData.number} : {classData.description}
                              </div>
                            </div>
                          </div>
                          {onAddClass && (
                            <button
                              type="button"
                              onClick={(e) => handleAddClass(e, classData)}
                              className="ml-2 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center"
                              title="Add this class"
                            >
                              <PlusIcon className="h-3 w-3 mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              No classes found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassAutocomplete; 